import React, { useContext, useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Body1, Heading3 } from '@/components/ui/typography'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientStatus } from 'api/Clients/Types/ClientStatus'
import { StatusReason } from 'api/Clients/Types/StatusReason'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AxiosError } from 'axios'
import { Box, FlexBox } from 'components/Box'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Link } from 'components/Link'
import { useTabNavigate } from 'hooks/useTabNavigate'
import { ChevronDown, Users } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchClientReasonStatusAction,
  fetchClientStatusAction,
} from 'store/clients/clients.actions'
import { instanceOfAxiosError, useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { ClientStatusId, ClientTypeEnum, typeOfClient } from 'utils/clients.helpers'
import { getClientExternalSystems } from 'utils/externalSystems'
import { links } from 'utils/featureToggles'
import { isNotEmpty } from 'utils/is-empty'
import { hasPermission, hasSomePermissions } from 'utils/permissionsValidator'

import BlockIcon from '../../../../assets/images/block.png'
import WarningIcon from '../../../../assets/images/warning.png'
import ClientAreaDuplicateGrid from '../Details/ClientAreaDuplicateGrid'
import ClientAreaRelationGrid from '../Details/ClientAreaRelationGrid'
import HistoryDetailsModal from '../Details/HistoryDetailsModal'
import { ExternalLink } from './ExternalLink'

import styles from './ClientNavigation.module.scss'

enum OnboardinKey {
  Standard = '1-false',
  Simplified = '1-true',
  DepositBeforeKYC = '2-false',
  SimplifiedDepositBeforeKYC = '2-true',
}

const getComponentByType = (
  onboardingFlowType: number,
  isVerificationExpedited: boolean
): JSX.Element => {
  const componentMap: Record<OnboardinKey, JSX.Element> = {
    [OnboardinKey.Standard]: <Badge variant='outline-primary'>Standard</Badge>,
    [OnboardinKey.Simplified]: <Badge variant='outline-primary'>Simplified</Badge>,
    [OnboardinKey.DepositBeforeKYC]: <Badge variant='outline-info'>Deposit before KYC</Badge>,
    [OnboardinKey.SimplifiedDepositBeforeKYC]: (
      <Badge variant='outline-info'>Deposit before KYC - Simplified</Badge>
    ),
  }
  const key = `${onboardingFlowType}-${isVerificationExpedited.toString()}` as OnboardinKey
  return componentMap[key] || null
}

interface Props {
  data: ClientDetailedDto
  isEdit: boolean
  tab: ClientTab

  updateClientStatus?: (values: UpdateClientStatus) => void
}

export enum ClientTab {
  INDIVIDUAL = 'details',
  CORPORATE = 'corporate-details',
  WALLETS = 'wallets',
  INTEREST_RATES = 'interest-rates',
  STATISTICS = 'statistics',
  BANK_ACCOUNTS = 'payment-details',
  TRANSACTIONS = 'transactions',
  CLASSIFICATION = 'classification',
  DOCUMENTS = 'documents',
  NOTES = 'notes',
  TESTS = 'tests',
  TESTS_KYC = 'tests-kyc',
  IB_INFO = 'introducing-broker',
  TICKETS = 'tickets',
  POA = 'poa',
  OTHER = 'other',
  CONTACT = 'contact',
  KYC = 'kyc',
  FUNDING_SUMMARY = 'funding-summary',
  SUBSCRIPTIONS = 'subscriptions',
}

export const ClientNavigation: React.FC<Props> = (props) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)

  const [reasonInput, setReasonInput] = useState<string | null>(null)
  const [tempReason, setTempReason] = useState<StatusReason | null>(null)

  const status = useAppSelector((state) => state.clients.status)
  const duplicates = useAppSelector((state) => state.clients.duplicates)
  const relations = useAppSelector((state) => state.clients.relations)
  const statusReason = useAppSelector((state) => state.clients.reason)

  const permissions = auth?.permissions || []
  const [showHistoryModal, setHistoryModal] = useState(false)
  const [duplicateModal, setDuplicateModal] = useState<boolean>(false)
  const [relationModal, setRelationModal] = useState<boolean>(false)

  const tabNavigate = useTabNavigate()

  const { data, isEdit } = props
  const [confirmationModal, setConfirmationModal] = useState<boolean>(props.isEdit)
  const [cannotChangeStatusModal, setCannotChangeStatusModal] = useState<boolean>(false)
  const [keyToChange, setKeyToChange] = useState<string>(ClientTab.INDIVIDUAL)

  const dispatch = useAppDispatch()

  const [statusChangeModal, setStatusChangeModal] = useState<{
    visible: boolean
    status?: ClientStatus
    reason?: StatusReason
    action?: () => void
  }>({
    visible: false,
  })

  useEffect(() => {
    dispatch(fetchClientReasonStatusAction(clientsApiClient))
    dispatch(fetchClientStatusAction(clientsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = async (key: string) => {
    setKeyToChange(key)
    if (isEdit) setConfirmationModal(true)
    else tabNavigate(key)
  }

  const handleTabChange = (reason: boolean) => {
    if (reason) tabNavigate(keyToChange)
    setConfirmationModal(false)
  }

  const setClientStatus = async (statReason: StatusReason) => {
    if (statReason.name === 'Other') {
      setTempReason(statReason)
      setReasonInput(' ')
    } else {
      if (data) {
        const updateStatusData: UpdateClientStatus = {
          id: data.id,
          reason: null,
          status: statReason.status,
          statusReason: {
            id: statReason.id,
            name: statReason.name,
          },
        }

        try {
          await clientsApiClient.updateClientStatus(updateStatusData)
          props.updateClientStatus?.(updateStatusData)
        } catch (err: unknown) {
          const error = err as { response: AxiosError<{ code?: string }> }
          if (instanceOfAxiosError(error?.response)) {
            const errorCode = error.response.response?.data?.code
            if (errorCode === 'cannot_change_client_status') {
              setCannotChangeStatusModal(true)
            }
          }
        }
      }
    }
  }
  const tabs = useMemo(
    () => [
      {
        key: ClientTab.INDIVIDUAL,
        label: 'Individual',
        permission: PermissionsEnum.CanViewClientInformation,
        visible: true,
      },
      {
        key: ClientTab.CONTACT,
        label: 'Contact',
        permission: PermissionsEnum.CanViewClientContact,
        visible: true,
      },
      {
        key: ClientTab.CORPORATE,
        label: 'Corporate',
        permission: PermissionsEnum.CanViewClientInformation,
        visible: typeOfClient(data.type) === ClientTypeEnum.COMPANY,
      },
      {
        key: ClientTab.WALLETS,
        label: 'Accounts',
        permission: PermissionsEnum.CanViewClientWalletsAccounts,
        visible: true,
      },
      {
        key: ClientTab.INTEREST_RATES,
        label: 'Interest Rate',
        permission: PermissionsEnum.CanBrowseClientsInterestRates,
        visible: true,
      },
      {
        key: ClientTab.BANK_ACCOUNTS,
        label: 'Payment Details',
        visible: hasSomePermissions(permissions, [
          PermissionsEnum.CanViewClientAccounts,
          PermissionsEnum.CanBrowseTokenizedCardDetails,
        ]),
      },
      {
        key: ClientTab.STATISTICS,
        label: 'Statistics',
        permission: PermissionsEnum.CanViewClientAccountStatistics,
        visible: true,
      },
      {
        key: ClientTab.TRANSACTIONS,
        label: 'Transactions',
        permission: PermissionsEnum.CanViewClientAccountTransactions,
        visible: true,
      },
      {
        key: ClientTab.FUNDING_SUMMARY,
        label: 'Funding Summary',
        permission: PermissionsEnum.CanViewFundingSummary,
        visible: true,
      },
      {
        key: ClientTab.NOTES,
        label: 'Notes',
        permission: PermissionsEnum.CanViewClientNotes,
        visible: true,
      },
      {
        key: ClientTab.DOCUMENTS,
        label: 'Documents',
        permission: PermissionsEnum.CanViewClientDocuments,
        visible: true,
      },
      {
        key: ClientTab.TESTS,
        label: 'Tests',
        permission: PermissionsEnum.CanViewClientTests,
        visible: true,
      },
      {
        key: ClientTab.TICKETS,
        label: 'Tickets',
        permission: PermissionsEnum.CanViewClientTickets,
        visible: true,
      },
      {
        key: ClientTab.POA,
        label: 'POA',
        permission: PermissionsEnum.CanViewClientPowerOfAttorney,
        visible: true,
      },
      {
        key: ClientTab.OTHER,
        label: 'Other',
        permission: PermissionsEnum.CanViewClientOtherInformation,
        visible: true,
      },
      {
        key: ClientTab.SUBSCRIPTIONS,
        label: 'Subscriptions',
        permission: PermissionsEnum.CanViewClientSubscriptions,
        visible: true,
      },
    ],
    [data.type, permissions]
  )

  const visibleTabs = tabs.filter(
    (tab) => tab.visible && hasPermission(permissions, tab.permission)
  )

  const needsStatusChangeConfirmation = (clientStatus: ClientStatus) =>
    clientStatus.id === ClientStatusId.DORMANT ||
    clientStatus.id === ClientStatusId.VALIDATION_DENIED ||
    clientStatus.id === ClientStatusId.BLOCKED

  const renderMenu = () => {
    return (
      <DropdownMenuContent className='w-auto' align='end'>
        {status.map((x) => (
          <DropdownMenuSub key={`status-${x.id}`}>
            <DropdownMenuSubTrigger data-test='status-menu-item'>{x.name}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {statusReason.map((reason) => {
                if (reason.status.id === x.id) {
                  if (reason.status.id === x.id) {
                    if (needsStatusChangeConfirmation(x)) {
                      return (
                        <DropdownMenuItem
                          key={`status-${x.id}-reason-${reason.id}`}
                          onSelect={() =>
                            setStatusChangeModal({
                              visible: true,
                              action: () => setClientStatus(reason),
                              status: x,
                              reason,
                            })
                          }
                        >
                          {reason.name}
                        </DropdownMenuItem>
                      )
                    }
                  }
                  return (
                    <DropdownMenuItem
                      key={`status-${x.id}-reason-${reason.id}`}
                      onSelect={() => setClientStatus(reason)}
                    >
                      {reason.name}
                    </DropdownMenuItem>
                  )
                }
                // eslint-disable-next-line array-callback-return
                return
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    )
  }

  return (
    <div>
      {statusChangeModal.visible && statusChangeModal.action && (
        <ConfirmationModal
          contentText={`Are you sure you wish to change client status to: ${statusChangeModal.status?.name} for the reason: ${statusChangeModal.reason?.name}?`}
          yesButtonText='Yes, Proceed'
          onYes={() => {
            statusChangeModal.action?.()
            setStatusChangeModal({ visible: false })
          }}
          onCancel={() => setStatusChangeModal({ visible: false })}
          visible={statusChangeModal.visible}
        />
      )}
      {cannotChangeStatusModal && (
        <ConfirmationModal
          renderTitle={
            <>
              <Heading3>Client balance not zero</Heading3>
              <br></br>
              <Body1>
                The client’s remaining balance must be 0.00 before you can change the status to
                Closed, Blocked, or Validation Denied.
              </Body1>
            </>
          }
          yesButtonText='Got it'
          onYes={() => setCannotChangeStatusModal(false)}
          visible={cannotChangeStatusModal}
          onClose={() => setCannotChangeStatusModal(false)}
        />
      )}
      {isNotEmpty(duplicates) && duplicateModal && (
        <Modal
          visible={duplicateModal}
          overflowY='auto'
          width={1100}
          onClose={() => setDuplicateModal(false)}
        >
          <ClientAreaDuplicateGrid duplicates={duplicates} />
        </Modal>
      )}
      {isNotEmpty(relations) && relationModal && (
        <Modal
          visible={relationModal}
          overflowY='auto'
          width={1100}
          onClose={() => setRelationModal(false)}
        >
          <ClientAreaRelationGrid relations={relations} />
        </Modal>
      )}
      {showHistoryModal && (
        <HistoryDetailsModal
          id={data.id}
          type={'client'}
          hideModal={() => setHistoryModal(false)}
        />
      )}
      <ConfirmationModal
        contentText='The page is asking you to confirm that you want to leave - data you have enter may not be saved.'
        yesButtonText='Leave Page'
        cancelButtonText='Stay on page'
        onYes={() => handleTabChange(true)}
        onCancel={() => handleTabChange(false)}
        visible={confirmationModal}
      />
      {reasonInput && data && tempReason && (
        <Modal width={400} visible={!!reasonInput} onClose={() => setReasonInput(null)}>
          <Box display='flex' flexDirection='column' className={styles.reasonBox}>
            <div className={styles.innerReasonBox}>
              <label className={styles.label}>Reason</label>
              <Input
                onChange={(e) => setReasonInput(e.target.value)}
                type='text'
                value={reasonInput}
              />
            </div>
            <FlexBox alignItems={'center'} justifyContent={'center'}>
              <Button
                className={styles.saveBtn}
                onClick={async () => {
                  const updateStatusData: UpdateClientStatus = {
                    id: data.id,
                    reason: reasonInput?.trimLeft(),
                    status: tempReason.status,
                    statusReason: {
                      id: tempReason.id,
                      name: tempReason.name,
                    },
                  }

                  try {
                    await clientsApiClient.updateClientStatus(updateStatusData)
                    if (props.updateClientStatus) props.updateClientStatus(updateStatusData)
                    setReasonInput(null)
                    setTempReason(null)
                  } catch (err: unknown) {
                    const error = err as { response: AxiosError<{ code?: string }> }
                    if (instanceOfAxiosError(error?.response)) {
                      const errorCode = error.response.response?.data?.code
                      if (errorCode === 'cannot_change_client_status') {
                        setReasonInput(null)
                        setTempReason(null)
                        setCannotChangeStatusModal(true)
                      }
                    }
                  }
                }}
              >
                {'Save'}
              </Button>
            </FlexBox>
          </Box>
        </Modal>
      )}
      <NavigationHeader>
        <ClientNavigationTitle data={data} />
        <CompanyAndExternalSystems data={data} />
        {data.status && (
          <Body1 bold className='ml-auto gap-2 flex items-center'>
            {isNotEmpty(relations) && (
              <span className='cursor-pointer' onClick={() => setRelationModal(true)}>
                <img width={24} height={24} alt='Relations' src={BlockIcon} />
              </span>
            )}
            {isNotEmpty(duplicates) && (
              <span className='cursor-pointer' onClick={() => setDuplicateModal(true)}>
                <img width={24} height={24} alt='warning' src={WarningIcon} />
              </span>
            )}
            Status:
            <Badge variant='status' onClick={() => setHistoryModal(true)}>
              {data.status.name}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    aria-label='More options'
                    disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientStatus)}
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>

              {renderMenu()}
            </DropdownMenu>
          </Body1>
        )}
      </NavigationHeader>
      <Tabs value={props.tab} onValueChange={handleTabClick}>
        <TabsList>
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  )
}

const CompanyAndExternalSystems = ({ data }: { data: ClientDetailedDto }) => {
  const OnboardingFlow = getComponentByType(
    data.visibilityConfiguration.onboardingFlowType.id,
    data.isVerificationExpedited
  )
  const externalSystems = useMemo(() => getClientExternalSystems(data), [data])
  const orderedExternalSystems = [...externalSystems].sort(
    (a, b) => a.externalSystem.id - b.externalSystem.id
  )
  return (
    <div className='flex items-center gap-2 pl-4'>
      <CompanyTag id={data.tickmillCompany.id}>{data.tickmillCompany.name}</CompanyTag>
      {data.clientIntroducingBroker && (
        <Link
          to={{
            pathname: generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
              id: data.clientIntroducingBroker.id,
              slug: 'details',
            }),
          }}
        >
          <CompanyTag id={'IB'}>{data.clientIntroducingBroker.name}</CompanyTag>
        </Link>
      )}
      {isNotEmpty(orderedExternalSystems) &&
        orderedExternalSystems.map((system, index) => (
          <ExternalLink
            size={24}
            key={system.id || `${system.externalSystem.id}-${index}`}
            data={system}
          />
        ))}
      {data.clientPaymentAgent && (
        <Link
          to={{
            pathname: generatePath(links.recordsPaymentAgents.detailsUrl as string, {
              id: data.clientPaymentAgent.id,
            }),
          }}
        >
          <CompanyTag id={'ClientPaymentAgent'}>{data.clientPaymentAgent.name}</CompanyTag>
        </Link>
      )}
      {data.leadId && (
        <Link
          to={{
            pathname: generatePath(links.recordsLeads.detailsUrl as string, {
              id: data.leadId,
              slug: 'details',
            }),
          }}
        >
          <Users size={24} />
        </Link>
      )}
      {OnboardingFlow}
    </div>
  )
}

const ClientNavigationTitle = ({ data }: { data: ClientDetailedDto }) => {
  if (!data.companyDetails) {
    const firstName = data.firstname || ''
    const middleName = data.middlename || ''
    const lastName = data.lastname || ''
    return <Body1 bold>{`${firstName} ${middleName} ${lastName}`}</Body1>
  }

  return <Body1 bold>{data.companyDetails.name}</Body1>
}
